import { computed, ref, type Ref } from 'vue';
import { useClientAnalytics } from '@ilteducation/client-analytics';
import type { GameOptions } from '@ilteducation/quizter-types';

interface UseQuizterTrackingParams {
  gameId: Ref<string | null>;
  resourceId: Ref<string | null>;
  resourceIds: Ref<string[]>;
  gameOptions: Ref<GameOptions>;
  joinedPlayersCount: Ref<number>;
  numberOfQuestions: Ref<number | undefined>;
}

export function useQuizterTracking({
  gameId,
  resourceId,
  resourceIds,
  gameOptions,
  joinedPlayersCount,
  numberOfQuestions,
}: UseQuizterTrackingParams) {
  const isHost = ref(false);
  const gameStartEventSent = ref(false);
  const gameEndEventSent = ref(false);

  const gameSessionId = computed(() => {
    if (!gameId.value || !resourceId.value) return null;
    const date = new Date().toISOString().split('T')[0];
    return `${gameId.value}-${resourceId.value}-${date}`;
  });

  const { sendAnalyticsEvent } = useClientAnalytics();

  const sendGameStartEvent = () => {
    if (!isHost.value || gameStartEventSent.value || !gameSessionId.value) return;
    gameStartEventSent.value = true;

    sendAnalyticsEvent('QuizterGameEventType', {
      eventName: 'game_started',
      gameSessionId: gameSessionId.value,
      gameId: gameId.value!,
      resourceId: resourceId.value!,
      resourceIds: resourceIds.value,
      resourceCount: resourceIds.value.length,
      useScoring: gameOptions.value.useScoring,
      useTimer: gameOptions.value.useTimer,
      questionAnswerTimeout: gameOptions.value.questionAnswerTimeout,
      showAnswerRecap: gameOptions.value.showAnswerRecap,
      playerCount: joinedPlayersCount.value,
      totalQuestions: numberOfQuestions.value ?? 0,
    });
  };

  const sendGameEndEvent = () => {
    if (!isHost.value || gameEndEventSent.value || !gameSessionId.value) return;
    gameEndEventSent.value = true;

    sendAnalyticsEvent('QuizterGameEventType', {
      eventName: 'game_ended',
      gameSessionId: gameSessionId.value,
      gameId: gameId.value!,
      resourceId: resourceId.value!,
      resourceIds: resourceIds.value,
      resourceCount: resourceIds.value.length,
      useScoring: gameOptions.value.useScoring,
      useTimer: gameOptions.value.useTimer,
      questionAnswerTimeout: gameOptions.value.questionAnswerTimeout,
      showAnswerRecap: gameOptions.value.showAnswerRecap,
      playerCount: joinedPlayersCount.value,
      totalQuestions: numberOfQuestions.value ?? 0,
    });
  };

  const resetTracking = () => {
    isHost.value = false;
    gameStartEventSent.value = false;
    gameEndEventSent.value = false;
  };

  return {
    isHost,
    gameSessionId,
    sendGameStartEvent,
    sendGameEndEvent,
    resetTracking,
  };
}
